<template>
  <ChannelSection>
    <template #title>
      <slot name="title">{{ sectionLabel }}</slot>
    </template>

    <template #actions>
      <slot name="actions"></slot>
      <!--
        Plan 091 FIX #4: fan-visible carousel arrows. Shown in the new channel
        layout for every viewer (the Figma QUESTS header has two red circular
        ‹ › buttons), scrolling the full-bleed rail one page at a time. Hidden
        while loading (no rail to scroll yet) and on the legacy path so that DOM
        stays byte-for-byte unchanged. The host-only manage/+ button is kept.
      -->
      <ChannelRailArrows
        v-if="newLayout && !loading && contestsCasted.length > 0"
        :rail="railRef"
      />
      <ContestNewDropdown>
        <template #trigger>
          <!--
            Plan 091 FIX A (a11y button-name): this icon-only host control had no
            accessible name (axe critical button-name). Added an i18n aria-label
            and marked the decorative `+` glyph aria-hidden. Mirrors the labelled
            create-offer control in offers-section.vue.
          -->
          <Button
            v-if="channelStore.permissions.manageContests"
            type="button"
            variant="icon"
            class="btn-circle border-border-brand size-10"
            :aria-label="t('channel.sections.contests.createContest')"
          >
            <Icon
              class="text-text size-6"
              icon="ic:baseline-plus"
              aria-hidden="true"
            />
          </Button>
        </template>
      </ContestNewDropdown>
    </template>

    <!--
      Plan 091 FIX #5: empty-state. When there are no contests and we are not
      loading, the rail renders nothing, leaving a bare header that looks broken.
      Show a simple message instead so the QUESTS section reads as intentional.
    -->
    <p
      v-if="!loading && contestsCasted.length === 0"
      class="px-5 text-sm text-text-secondary"
    >
      {{ t("channel.sections.contests.empty") }}
    </p>

    <div v-else-if="!loading" class="relative">
      <!--
        In the new channel layout the QUESTS rail is a
        full-bleed carousel that overflows to the page-column region edges while
        the section header stays centered. The breakout is applied ONLY in the
        new-layout path (FullBleedRail :enabled); with the flag off it renders a
        transparent passthrough so the legacy rail DOM is byte-for-byte
        unchanged.
      -->
      <FullBleedRail :enabled="newLayout">
        <ChannelRailVirtualized
          ref="railRef"
          :items="contestsCasted"
          :get-key="(contest) => contest.id"
          :item-width="newLayout ? 361 : 312"
        >
          <template #default="{ item: { data: contest } }">
            <RaffleItemCard
              v-if="contest.__typename === 'Raffle'"
              :id="idify(contest.id)"
              :raffle="contest"
              :loading="loading"
              :authorization-rule-group-fragment="
                contest.authorization_rule_group
              "
              @join="refetch"
              @refetch="refetch"
            />
            <QuizItemCard
              v-else-if="contest.__typename === 'Quiz'"
              :id="idify(contest.id)"
              :quiz="contest"
              :loading="loading"
              :authorization-rule-group-fragment="
                contest.authorization_rule_group
              "
              @refetch="refetch"
            />
          </template>
        </ChannelRailVirtualized>
      </FullBleedRail>

      <div
        v-if="loadingMore"
        class="absolute right-0 top-0 bottom-0 flex items-center justify-center bg-gradient-to-l to-transparent pl-8 pr-5"
        :class="
          newLayout
            ? 'from-bg via-bg/80'
            : 'from-brand-bg-main via-brand-bg-main/80'
        "
      >
        <Spinner class="size-4" />
      </div>
    </div>

    <SkeletonRail v-else :count="3">
      <template #default="{ index }">
        <ContestCardSkeleton :index="index" />
      </template>
    </SkeletonRail>
  </ChannelSection>
</template>

<script setup lang="ts">
import RaffleItemCard, { RaffleItemCardFragment } from "./raffle-item-card.vue";
import ChannelSection from "../channel-section.vue";
import { computed, ref } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import QuizItemCard, { QuizItemCardFragment } from "./quiz-item-card.vue";
import ContestNewDropdown from "./creation/contest-new-dropdown.vue";
import Button from "@/components/ui/button/button.vue";
import { Icon } from "@iconify/vue/dist/iconify.js";
import { idify } from "@/utils/misc";
import ChannelRailVirtualized from "../channel-rail-virtualized.vue";
import ChannelRailArrows from "../channel-rail-arrows.vue";
import FullBleedRail from "../full-bleed-rail.vue";
import { useFeatureFlags } from "@/composables/use-feature-flags";
import { useInfiniteScroll } from "@vueuse/core";
import SkeletonRail from "@/components/ui/skeleton/skeleton-rail.vue";
import ContestCardSkeleton from "@/components/ui/skeleton/contest-card-skeleton.vue";
import Spinner from "@/components/ui/spinner.vue";
import { useAppStore } from "@/stores/app.store";
import { useI18n } from "vue-i18n";
import { ContestsForChannelDocument } from "@/generated/graphql";
import type { ResultOf } from "@graphql-typed-document-node/core";

type RaffleItemCardData = ResultOf<typeof RaffleItemCardFragment>;
type QuizItemCardData = ResultOf<typeof QuizItemCardFragment>;

const appStore = useAppStore();
const { t } = useI18n();

const channelStore = useChannelStore();
const railRef = ref<HTMLElement | null>(null);

const { getBoolean } = useFeatureFlags();
const newLayout = getBoolean("enable_new_channel_layout");

const emit = defineEmits<{
  refetch: [];
  loadMore: [];
}>();

const props = withDefaults(
  defineProps<{
    loading: boolean;
    contests: ResultOf<typeof ContestsForChannelDocument>["contestsForChannel"];
    loadingMore?: boolean;
    /**
     * Whether more pages remain to load. Gates the infinite-scroll trigger:
     * an empty / short rail has no scrollable overflow so it sits permanently
     * within `distance` of the right edge and would otherwise emit `loadMore`
     * every `interval`ms forever. `canLoadMore` suppresses the trigger when
     * there is nothing more to load or a load is already in flight. Defaults to
     * true so a parent that does not pass it keeps prior (ungated) behaviour.
     */
    hasMore?: boolean;
  }>(),
  { hasMore: true },
);

useInfiniteScroll(
  railRef,
  () => {
    emit("loadMore");
  },
  {
    direction: "right" as const,
    distance: 100,
    interval: 300,
    canLoadMore: () => props.hasMore && !props.loadingMore,
  },
);

// Cast contests to properly typed union
const contestsCasted = computed(() => {
  return (
    props.contests?.map((contest) => {
      if (contest.__typename === "Raffle") {
        return contest as RaffleItemCardData;
      } else {
        return contest as QuizItemCardData;
      }
    }) ?? []
  );
});

function refetch() {
  emit("refetch");
}

const sectionLabel = computed(() => {
  if (appStore.isEmbed) {
    return t("channel.titles.embedContests");
  }
  return t("channel.titles.contests");
});
</script>
